/**
 * DAD LINK soundscape — synthesized WebAudio (same idiom as chime.ts): no audio
 * assets, nothing to 404 on the kiosk, and it can never throw (sound is garnish,
 * not function). One instance per overlay mount; dispose() on unmount.
 *
 *   powerUp(stage) — rising "system energizing" sweep, pitch escalates per boot
 *                    stage (0 encrypted link → 1 satellites → 2 scan)
 *   satBlip()      — bright tick when a satellite flips to LINKED
 *   startRing()    — continuous sonar ping (~1.6s period) while it searches, so
 *                    the ringing is AUDIBLE, not just the chips
 *   found()/lost() — resolution tones (both stop the ping first)
 */
export type DadLinkAudio = ReturnType<typeof createDadLinkAudio>;

export function createDadLinkAudio() {
  let ctx: AudioContext | null = null;
  let ringTimer: number | undefined;

  const ac = (): AudioContext | null => {
    try {
      if (!ctx) ctx = new AudioContext();
      ctx.resume().catch(() => {});
      return ctx;
    } catch {
      return null;
    }
  };
  const nowT = () => {
    const c = ac();
    return c ? c.currentTime + 0.02 : 0;
  };
  const tone = (
    freq: number,
    t0: number,
    dur: number,
    gain: number,
    type: OscillatorType = "sine",
    sweepTo?: number
  ) => {
    const c = ac();
    if (!c || !t0) return;
    try {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, t0);
      if (sweepTo) o.frequency.exponentialRampToValueAtTime(sweepTo, t0 + dur);
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(gain, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
      o.connect(g);
      g.connect(c.destination);
      o.start(t0);
      o.stop(t0 + dur + 0.05);
    } catch {
      /* never let sound break the call flow */
    }
  };

  return {
    powerUp(stage: number) {
      const t0 = nowT();
      if (!t0) return;
      const base = 90 * Math.pow(1.5, stage + 1); // 135 → 202 → 304 Hz
      tone(base, t0, 0.7, 0.16, "sine", base * 3.2); // rising energize sweep
      tone(base * 4, t0 + 0.42, 0.25, 0.07, "triangle"); // settle chirp
    },
    satBlip() {
      const t0 = nowT();
      if (!t0) return;
      tone(1567.98, t0, 0.12, 0.09, "square");
      tone(2093.0, t0 + 0.09, 0.1, 0.055, "square");
    },
    startRing() {
      if (ringTimer) return;
      const ping = () => {
        const t0 = nowT();
        if (!t0) return;
        tone(1244.51, t0, 0.42, 0.12); // sonar ping
        tone(1108.73, t0 + 0.16, 0.5, 0.04); // faint echo
      };
      ping();
      ringTimer = window.setInterval(ping, 1600);
    },
    stopRing() {
      if (ringTimer) {
        window.clearInterval(ringTimer);
        ringTimer = undefined;
      }
    },
    found() {
      this.stopRing();
      const t0 = nowT();
      if (!t0) return;
      tone(659.25, t0, 0.28, 0.15);
      tone(987.77, t0 + 0.16, 0.5, 0.15);
    },
    lost() {
      this.stopRing();
      const t0 = nowT();
      if (!t0) return;
      tone(392, t0, 0.5, 0.13, "sine", 220);
      tone(196, t0 + 0.4, 0.8, 0.09);
    },
    dispose() {
      this.stopRing();
      try {
        ctx?.close();
      } catch {
        /* ignore */
      }
      ctx = null;
    },
  };
}
